import { Inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { CmLocalizationService, LocalizedText } from '@ats/cm/core';
import { LocalizationService } from '@cm/core/localization';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

export const DEFAULT_PAGE_TITLE_KEY = 'DEFAULT_TITLE_KEY';
export const DEFAULT_PAGE_TITLE_VALUE = 'DEFAULT_TITLE_VALUE';

@UntilDestroy()
@Injectable({
	providedIn: 'root',
})
export class PageTitleService {
	private readonly title = new BehaviorSubject<LocalizedText | string | null>(null);

	constructor(
		@Inject(DEFAULT_PAGE_TITLE_KEY) private readonly defaultKey: string,
		@Inject(DEFAULT_PAGE_TITLE_VALUE) private readonly defaultValue: string,
		private readonly locService: LocalizationService,
		private readonly legacyLocalizationService: CmLocalizationService,
		private readonly titleService: Title
	) {
		const localizedCompanyName$ = this.legacyLocalizationService.localize$(
			this.defaultKey,
			this.defaultValue
		);

		const title$ = this.title.asObservable().pipe(
			switchMap((title: LocalizedText | string) => {
				if (!title) {
					return of(null);
				}
				return typeof title === 'string'
					? this.locService.findValue$(title)
					: this.legacyLocalizationService.localize$(title.key, title.defaultValue);
			}),
			map(title => (title ? `${title} | ` : ''))
		);

		combineLatest([localizedCompanyName$, title$])
			.pipe(
				untilDestroyed(this),
				map(([companyName, title]) => `${title}${companyName}`)
			)
			.subscribe(title => this.titleService.setTitle(title));
	}

	setTitle(title: LocalizedText | string | null = null): void {
		this.title.next(title);
	}
}
